import { useSelector } from 'react-redux';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';

import {
  SimilarBreedSeeAllResultsResource,
  SimilarBreedsSeeAllBreederRowResource
} from '../constants/resource_types';
import { similarBreedSeeAllResultsResourceDefinition } from '../utils/queryDefinitions';
import {
  SEE_ALL_RESULTS_PER_PAGE,
  SimilarBreedsResultsType
} from '../utils/similarBreeds';

interface UseSimilarBreedSeeAllResultsArgs {
  /** The overlay is open. Nothing is fetched while it is closed. */
  enabled: boolean;
  /** The module's filters, verbatim, so both surfaces agree on the selection. */
  filters: Record<string, unknown>;
  resultsType: SimilarBreedsResultsType;
  page: number;
}

export default function useSimilarBreedSeeAllResults({
  enabled,
  filters,
  resultsType,
  page
}: UseSimilarBreedSeeAllResultsArgs) {
  const breedSlug = useSelector(({ server }: any) => server.breedSlug);

  const query = useResourceQuery<SimilarBreedSeeAllResultsResource>({
    ...similarBreedSeeAllResultsResourceDefinition(breedSlug, {
      ...filters,
      results_type: resultsType,
      page,
      per_page: SEE_ALL_RESULTS_PER_PAGE
    }),
    enabled: enabled && Boolean(breedSlug)
  });

  const meta = query.value.meta || {};
  const data = query.value.data || {};

  return {
    isLoading: query.isLoading,
    // One row per breeder either way - the tabs pick a serialization, not a
    // different selection - so this is the page count for both tabs.
    totalItemCount: meta.total_item_count || 0,
    // Every qualifying puppy in the radius, before the one-per-breeder
    // selection - so larger than the row count above, and the other half of
    // what `Breed Searched` reports.
    totalPuppyCount: meta.total_puppy_count || 0,
    radiusInMiles: meta.radius_in_miles,
    puppies: (data.puppies || []) as any[],
    breeders: (data.breeders || []) as SimilarBreedsSeeAllBreederRowResource[]
  };
}
